// src/components/RichiestaCommercialeTab.js
import React, { useState, useEffect, useRef } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, getDoc, setDoc, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { prossimoProgressivo } from "../utils/contatoriUtils";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { Plus, Trash2, Printer, X, ChevronDown, ChevronUp } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import { dominioSito } from "../utils/fornitore";
import { numIt } from "../utils/economiaCommessa";
import SopralluoghiDellaRC from "./SopralluoghiDellaRC";

export const STATI_RC = {
  emessa:        { label: "Emessa",        bg: "#dbeafe", col: "#1d4ed8" },
  in_lavorazione:{ label: "In lavorazione",bg: "#fef3c7", col: "#92400e" },
  annullata:     { label: "Annullata",     bg: "#f3f4f6", col: "#9ca3af" },
  // Fallback retrocompatibilità stati vecchi
  bozza:           { label: "Emessa",     bg: "#dbeafe", col: "#1d4ed8" },
  inviata:         { label: "Emessa",     bg: "#dbeafe", col: "#1d4ed8" },
  presa_in_carico: { label: "In lavorazione", bg: "#fef3c7", col: "#92400e" },
  in_elaborazione: { label: "In lavorazione", bg: "#fef3c7", col: "#92400e" },
  offerta_emessa:  { label: "Emessa",     bg: "#dbeafe", col: "#1d4ed8" },
  accettata:       { label: "Emessa",     bg: "#dbeafe", col: "#1d4ed8" },
  rifiutata:       { label: "Annullata",  bg: "#f3f4f6", col: "#9ca3af" },
};

function fmt(n) {
  return (parseFloat(n)||0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function calcolaVoce(v) {
  const totFornitori = (v.fornitori||[]).reduce((s,f)=>s+numIt(f.importo),0);
  const totTrasporto = (parseFloat(v.km)||0)*(parseFloat(v.nViaggi)||1)*(parseFloat(v.costoKm)||0)+(parseFloat(v.autostrada)||0);
  const totManodopera = (parseFloat(v.qtaOre)||0)*(parseFloat(v.costoOra)||0);
  const totDiretto = totFornitori + totTrasporto + totManodopera;
  const totCG = totDiretto * (parseFloat(v.percCostiGenerali)||0) / 100;
  const totCosti = totDiretto + totCG;
  const valoreOfferta = totCosti * (1 + (parseFloat(v.percMarkup)||0)/100);
  return { totFornitori, totTrasporto, totManodopera, totDiretto, totCG, totCosti, valoreOfferta };
}

function VoceCard({ voce, idx, onChange, onDelete, nomeAzienda }) {
  const [aperta, setAperta] = useState(true);
  const upd = (k, v) => onChange({ ...voce, [k]: v });
  const updF = (i, k, v) => { const f=[...(voce.fornitori||[])]; f[i]={...f[i],[k]:v}; upd("fornitori",f); };
  const addF = () => upd("fornitori", [...(voce.fornitori||[]), { nome:"", descrizione:"", importo:"" }]);
  const removeF = i => upd("fornitori", (voce.fornitori||[]).filter((_,j)=>j!==i));

  const { totFornitori, totTrasporto, totManodopera, totCG, totCosti, valoreOfferta } = calcolaVoce(voce);

  const inp = { padding:"4px 8px", border:"1px solid #e5e7eb", borderRadius:6, fontSize:12, fontFamily:"'Inter',sans-serif", outline:"none", width:"100%", boxSizing:"border-box" };
  const num = { ...inp, textAlign:"right" };
  const lbl = { fontSize:10, fontWeight:600, color:"#6b7280", display:"block", marginBottom:1 };
  const g3 = { display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 };
  const g4 = { display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:10 };

  return (
    <div style={{ border:"1px solid #e5e7eb", borderRadius:12, marginBottom:12, overflow:"hidden", background:"#fff" }}>
      {/* Header voce */}
      <div style={{ padding:"6px 16px", background:"#f9fafb", display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}
        onClick={()=>setAperta(!aperta)}>
        <div style={{ width:28, height:28, borderRadius:8, background:"#1e1b4b", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800, flexShrink:0 }}>{idx+1}</div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#111827" }}>{voce.descrizione||<span style={{color:"#9ca3af",fontStyle:"italic"}}>Descrizione voce...</span>}</div>
          <div style={{ fontSize:11, color:"#9ca3af", marginTop:1 }}>
            {totFornitori>0 && <span style={{marginRight:10}}>Fornitori: €{fmt(totFornitori)}</span>}
            {totTrasporto>0 && <span style={{marginRight:10}}>Trasporto: €{fmt(totTrasporto)}</span>}
            {totManodopera>0 && <span style={{marginRight:10}}>Manodopera: €{fmt(totManodopera)}</span>}
          </div>
        </div>
        <div style={{ textAlign:"right", flexShrink:0 }}>
          <div style={{ fontSize:11, color:"#9ca3af" }}>Valore offerta</div>
          <div style={{ fontSize:18, fontWeight:900, color:"#059669" }}>€{fmt(valoreOfferta)}</div>
        </div>
        <div style={{ marginLeft:8, display:"flex", gap:6, alignItems:"center" }}>
          {aperta ? <ChevronUp size={14} color="#9ca3af"/> : <ChevronDown size={14} color="#9ca3af"/>}
          <button onClick={e=>{e.stopPropagation();onDelete();}} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5",padding:4}}>
            <Trash2 size={14}/>
          </button>
        </div>
      </div>

      {/* Corpo voce */}
      {aperta && (
        <div style={{ padding:"10px 16px" }}>
          {/* Descrizione e spesa a carico */}
          <div style={{ display:"grid", gridTemplateColumns:"1.4fr 0.6fr", gap:10, marginBottom:8 }}>
            <div>
              <label style={lbl}>Descrizione lavorazione *</label>
              <input style={inp} value={voce.descrizione||""} onChange={e=>upd("descrizione",e.target.value)} placeholder="Es. Fornitura e posa pompetta e valvola NR"/>
            </div>
            <div>
              <label style={lbl}>Spesa a carico di</label>
              <select style={inp} value={voce.spesaCarico||nomeAzienda||"Azienda"} onChange={e=>upd("spesaCarico",e.target.value)}>
                {[nomeAzienda||"Azienda","Cliente","Fornitore","Altro"].map(o=><option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          {/* Sezione Fornitori */}
          <div style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:8, padding:7, marginBottom:6 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:4 }}>📦 Costi Fornitori e Prestazioni</div>
            {(voce.fornitori||[]).map((f,i)=>(
              <div key={i} style={{ display:"flex", gap:6, marginBottom:4, alignItems:"flex-end" }}>
                <div style={{ flex:1.4 }}>
                  {i===0 && <label style={lbl}>Fornitore</label>}
                  <input style={inp} value={f.nome||""} onChange={e=>updF(i,"nome",e.target.value)} placeholder="Nome fornitore"/>
                </div>
                <div style={{ flex:3 }}>
                  {i===0 && <label style={lbl}>Descrizione materiali/servizio offerto</label>}
                  <input style={inp} value={f.descrizione||""} onChange={e=>updF(i,"descrizione",e.target.value)} placeholder="Es. Moduli FTV 450W, N.10 pz"/>
                </div>
                <div style={{ flex:0.8 }}>
                  {i===0 && <label style={lbl}>Importo (€)</label>}
                  <input style={num} type="number" min="0" step="0.01" value={f.importo||""} onChange={e=>updF(i,"importo",e.target.value)} placeholder="0,00"/>
                </div>
                <button onClick={()=>removeF(i)} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5",padding:"7px 4px",flexShrink:0,marginBottom:0}}>
                  <Trash2 size={14}/>
                </button>
              </div>
            ))}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:8 }}>
              <button onClick={addF} style={{fontSize:12,color:"#7c3aed",background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:4,fontFamily:"'Inter',sans-serif"}}>
                <Plus size={12}/> Aggiungi fornitore
              </button>
              {totFornitori>0 && (
                <div style={{ fontSize:13, fontWeight:800, color:"#374151" }}>
                  Totale fornitori: <span style={{color:"#7c3aed"}}>€{fmt(totFornitori)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Sezione Trasporto */}
          <div style={{ background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:8, padding:7, marginBottom:6 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#0369a1", marginBottom:4 }}>🚗 Trasporto e viaggio</div>
            <div style={{ ...g4, marginBottom:4 }}>
              <div>
                <label style={{...lbl,color:"#0369a1"}}>Km A/R</label>
                <input style={num} type="number" min="0" value={voce.km||""} onChange={e=>upd("km",e.target.value)} placeholder="0"/>
              </div>
              <div>
                <label style={{...lbl,color:"#0369a1"}}>N° viaggi</label>
                <input style={num} type="number" min="1" value={voce.nViaggi||"1"} onChange={e=>upd("nViaggi",e.target.value)} placeholder="1"/>
              </div>
              <div>
                <label style={{...lbl,color:"#0369a1"}}>€/km</label>
                <input style={num} type="number" min="0" step="0.01" value={voce.costoKm||""} onChange={e=>upd("costoKm",e.target.value)} placeholder="0,30"/>
              </div>
              <div>
                <label style={{...lbl,color:"#0369a1"}}>Autostrada (€)</label>
                <input style={num} type="number" min="0" step="0.01" value={voce.autostrada||""} onChange={e=>upd("autostrada",e.target.value)} placeholder="0,00"/>
              </div>
            </div>
            {(parseFloat(voce.km)||0)>0 && (
              <div style={{ fontSize:12, color:"#0369a1", fontWeight:700 }}>
                {voce.km} km × {voce.nViaggi||1} viaggio/i × €{voce.costoKm}/km{(parseFloat(voce.autostrada)||0)>0?` + €${voce.autostrada} autostrada`:""} = <span style={{fontSize:14}}>€{fmt(totTrasporto)}</span>
              </div>
            )}
          </div>

          {/* Sezione Manodopera */}
          <div style={{ background:"#f0fdf4", border:"1px solid #86efac", borderRadius:8, padding:7, marginBottom:6 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#166534", marginBottom:4 }}>👷 Manodopera</div>
            <div style={{ ...g3, marginBottom:4 }}>
              <div>
                <label style={{...lbl,color:"#166534"}}>Quantità (ore/n°)</label>
                <input style={num} type="number" min="0" step="0.5" value={voce.qtaOre||""} onChange={e=>upd("qtaOre",e.target.value)} placeholder="0"/>
              </div>
              <div>
                <label style={{...lbl,color:"#166534"}}>Costo unitario (€)</label>
                <input style={num} type="number" min="0" step="0.5" value={voce.costoOra||""} onChange={e=>upd("costoOra",e.target.value)} placeholder="0,00"/>
              </div>
              <div>
                <label style={{...lbl,color:"#166534"}}>Totale manodopera</label>
                <div style={{ ...num, background:"#dcfce7", fontWeight:800, color:"#166534", display:"flex", alignItems:"center", border:"1px solid #86efac" }}>
                  €{fmt(totManodopera)}
                </div>
              </div>
            </div>
            {(parseFloat(voce.qtaOre)||0)>0 && (
              <div style={{ fontSize:12, color:"#166534" }}>
                {voce.qtaOre} × €{voce.costoOra} = <strong>€{fmt(totManodopera)}</strong>
              </div>
            )}
          </div>

          {/* Costi generali e Markup */}
          <div style={{ background:"#fafafa", border:"1px solid #e5e7eb", borderRadius:8, padding:7 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:4 }}>📊 Riepilogo economico voce</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr", gap:10, alignItems:"end" }}>
              <div>
                <label style={lbl}>Costo diretto</label>
                <div style={{ ...num, background:"#f3f4f6", fontWeight:700, color:"#374151", display:"flex", alignItems:"center", border:"1px solid #e5e7eb" }}>€{fmt(calcolaVoce(voce).totDiretto)}</div>
              </div>
              <div>
                <label style={lbl}>Costi generali (%)</label>
                <input style={num} type="number" min="0" step="0.5" value={voce.percCostiGenerali||""} onChange={e=>upd("percCostiGenerali",e.target.value)} placeholder="0"/>
              </div>
              <div>
                <label style={lbl}>Importo costi gen.</label>
                <div style={{ ...num, background:"#f3f4f6", fontWeight:700, color:"#374151", display:"flex", alignItems:"center", border:"1px solid #e5e7eb" }}>€{fmt(totCG)}</div>
              </div>
              <div>
                <label style={lbl}>Markup (%)</label>
                <input style={num} type="number" min="0" step="0.5" value={voce.percMarkup||""} onChange={e=>upd("percMarkup",e.target.value)} placeholder="30"/>
              </div>
              <div>
                <label style={{...lbl,color:"#059669"}}>Valore offerta</label>
                <div style={{ ...num, background:"#d1fae5", fontWeight:900, color:"#059669", fontSize:16, display:"flex", alignItems:"center", border:"1px solid #86efac" }}>€{fmt(valoreOfferta)}</div>
              </div>
            </div>
          </div>

          {/* Note voce */}
          <div style={{ marginTop:6 }}>
            <label style={lbl}>Note voce</label>
            <input style={inp} value={voce.note||""} onChange={e=>upd("note",e.target.value)} placeholder="Note specifiche per questa voce..."/>
          </div>
        </div>
      )}
    </div>
  );
}

export function RCForm({ rc, projectId, projectName, codiceCommessa, clienteNome, clienti, orgId, orgInfo, onClose }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const [form, setForm] = useState(rc || {
    oggetto: "", sito: projectName||"", dataEmissione: toLocalDateStr(new Date()),
    stato: "emessa", assegnatarioUid: "", assegnatarioNome: "", note: "",
    clienteId: "", clienteNome: clienteNome||"",
  });
  const [voci, setVoci] = useState(rc?.voci || []);
  const [saving, setSaving] = useState(false);
  const [utenti, setUtenti] = useState([]);
  const upd = (k,v) => setForm(f=>({...f,[k]:v}));

  // Carica utenti org per assegnazione
  useEffect(()=>{
    if(!orgId) return;
    getDocs(query(collection(db,"users"),where("organizationId","==",orgId)))
      .then(snap=>setUtenti(snap.docs.map(d=>({uid:d.id,...d.data()})).filter(u=>u.enabled!==false)));
  },[orgId]);

  // la riga di calcolo iniziale si propone una volta sola, alla nuova richiesta
  const rcPresente = !!rc;
  const vociIniziali = useRef(voci.length);
  useEffect(()=>{
    if(rcPresente||!orgId||vociIniziali.current>0) return;
    getDoc(doc(db,"organizations",orgId)).then(snap=>{
      if(!snap.exists()) return;
      const d=snap.data();
      setVoci([{
        descrizione:"", spesaCarico: d.nome||d.name||"Azienda",
        fornitori:[{nome:"",importo:""}],
        km:"", nViaggi:"1", costoKm: d.costoKmDefault||0.30, autostrada:"",
        qtaOre:"", costoOra: d.costoOrarioBase||"",
        percCostiGenerali: d.costiGeneraliDefault ?? 10,
        percMarkup: d.markupDefault ?? 15, note:""
      }]);
    });
  },[orgId, rcPresente]);

  const addVoce = () => {
    const u=voci[voci.length-1];
    setVoci(v=>[...v,{
      descrizione:"", spesaCarico:orgInfo?.nome||orgInfo?.name||orgInfo?.name||"Azienda",
      fornitori:[{nome:"",importo:""}],
      km:"",nViaggi:"1",costoKm:u?.costoKm||0.30,autostrada:"",
      qtaOre:"",costoOra:u?.costoOra||"",
      percCostiGenerali:u?.percCostiGenerali ?? 10,
      percMarkup:u?.percMarkup ?? 15,note:""
    }]);
  };

  const totali = () => {
    let tC=0,tO=0;
    voci.forEach(v=>{ const c=calcolaVoce(v); tC+=c.totCosti; tO+=c.valoreOfferta; });
    return {totCosti:tC,totOff:tO};
  };

  const salva = async()=>{
    if(!form.oggetto.trim()){addToast("Inserisci l'oggetto","error");return;}
    setSaving(true);
    const {totCosti,totOff}=totali();
    const dati={
      ...form,voci,projectId:projectId||"",projectName:projectName||"",codiceCommessa:codiceCommessa||"",
      clienteNome: projectId ? (clienteNome||"") : (form.clienteNome||""),
      orgId,orgNome:orgInfo?.nome||orgInfo?.name||orgInfo?.name||"",
      totaleCosti:totCosti,totaleOfferta:totOff,
      autonoma: !projectId,
      updatedAt:serverTimestamp(),
    };
    if(rc?.id){
      await updateDoc(doc(db,"richiesteCommerciali",rc.id),dati);
      // Notifica se cambia assegnatario
      if(form.assegnatarioUid && form.assegnatarioUid !== rc.assegnatarioUid){
        await addDoc(collection(db,"notifiche"),{
          userId: form.assegnatarioUid, orgId,
          tipo: "richiesta_commerciale",
          titolo: "Richiesta commerciale assegnata",
          testo: `${currentUser?.displayName||"Il PM"} ti ha assegnato la richiesta ${rc.numero}: "${form.oggetto}"`,
          projectId: projectId||null, letta: false,
          createdAt: serverTimestamp(),
        });
      }
      addToast("Richiesta aggiornata ✅","success");
    } else {
      const anno=new Date().getFullYear();
      const prog = await prossimoProgressivo(`${orgId}_rc_${anno}`, { orgId, anno });
      const numero=`RC-${anno}-${String(prog).padStart(4,"0")}`;
      const nuovoDoc = await addDoc(collection(db,"richiesteCommerciali"),{...dati,numero,
        compilatoreUid:currentUser?.uid,compilatoreNome:currentUser?.displayName||"",
        createdAt:serverTimestamp()});
      // Notifica in-app all'assegnatario
      if(form.assegnatarioUid){
        await addDoc(collection(db,"notifiche"),{
          userId: form.assegnatarioUid, orgId,
          tipo: "richiesta_commerciale",
          titolo: "Nuova richiesta commerciale",
          testo: `${currentUser?.displayName||"Il PM"} ti ha assegnato la richiesta ${numero}: "${form.oggetto}"`,
          projectId: projectId||null, letta: false,
          createdAt: serverTimestamp(),
        });
      }
      addToast(`Richiesta ${numero} creata ✅`,"success");
    }
    setSaving(false);
    onClose();
  };

  const stampaPDF=()=>{
    const win=window.open("","_blank");
    const logoHtml=orgInfo?.logo?`<img src="${orgInfo.logo}" style="height:40px;object-fit:contain;"/>`:
      `<div style="font-size:18px;font-weight:900;color:#1e1b4b;">◆ ${orgInfo?.nome||orgInfo?.name||""}</div>`;
    const {totCosti,totOff}=totali();

    const vociHtml=voci.map((v,i)=>{
      const c=calcolaVoce(v);

      // Fornitori
      const fornitoriRows=(v.fornitori||[]).filter(f=>f.nome||f.importo||f.descrizione).map(f=>
        `<div style="display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid #f3f4f6;">
          <span style="font-size:10px;color:#374151">${f.nome||"—"}${f.descrizione?` <span style="color:#9ca3af">— ${f.descrizione}</span>`:""}</span>
          <span style="font-size:10px;font-weight:700;color:#374151;white-space:nowrap;padding-left:8px">€${fmt(f.importo)}</span>
        </div>`
      ).join("");

      // Formula trasporto
      const formulaTrasporto = v.km
        ? `${v.km} km × ${v.nViaggi||1} viaggio/i × €${v.costoKm}/km${v.autostrada?` + €${v.autostrada} autostrada`:""} = <strong>€${fmt(c.totTrasporto)}</strong>`
        : "—";

      // Formula manodopera
      const formulaMano = v.qtaOre
        ? `${v.qtaOre} × €${v.costoOra} = <strong>€${fmt(c.totManodopera)}</strong>`
        : "—";

      return `<div style="border:1px solid #e5e7eb;border-radius:10px;margin-bottom:14px;overflow:hidden;page-break-inside:avoid;">

        <!-- Header voce -->
        <div style="background:#1e1b4b;padding:9px 14px;display:flex;justify-content:space-between;align-items:center;">
          <div style="display:flex;align-items:center;gap:10px;">
            <span style="background:#fff;color:#1e1b4b;width:22px;height:22px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;font-size:11px;font-weight:900;flex-shrink:0">${i+1}</span>
            <span style="color:#fff;font-weight:700;font-size:13px">${v.descrizione||"—"}</span>
          </div>
          <div>
            <span style="color:rgba(255,255,255,.55);font-size:10px;margin-right:12px">Spesa a carico: ${v.spesaCarico||"—"}</span>
            <span style="color:#4ade80;font-weight:900;font-size:16px">€${fmt(c.valoreOfferta)}</span>
          </div>
        </div>

        <!-- Corpo: 3 sezioni operative -->
        <div style="display:grid;grid-template-columns:1.4fr 0.6fr 1fr;border-bottom:1px solid #e5e7eb;">

          <!-- Fornitori -->
          <div style="padding:12px 14px;border-right:1px solid #e5e7eb;">
            <div style="font-size:9px;color:#6b7280;font-weight:700;text-transform:uppercase;letter-spacing:.05em;margin-bottom:8px">📦 Costi Fornitori e Prestazioni</div>
            ${fornitoriRows || `<div style="font-size:10px;color:#d1d5db">Nessun fornitore</div>`}
            ${c.totFornitori>0?`<div style="font-size:11px;font-weight:800;color:#374151;margin-top:6px;padding-top:4px;border-top:1px solid #e5e7eb;text-align:right">Totale fornitori: <span style="color:#7c3aed">€${fmt(c.totFornitori)}</span></div>`:""}
          </div>

          <!-- Trasporto -->
          <div style="padding:12px 14px;border-right:1px solid #e5e7eb;background:#f0f9ff;">
            <div style="font-size:9px;color:#0369a1;font-weight:700;text-transform:uppercase;letter-spacing:.05em;margin-bottom:8px">🚗 Trasporto e viaggio</div>
            ${v.km?`
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:6px;">
              <div><div style="font-size:8px;color:#9ca3af">Km A/R</div><div style="font-size:11px;font-weight:600">${v.km}</div></div>
              <div><div style="font-size:8px;color:#9ca3af">N° viaggi</div><div style="font-size:11px;font-weight:600">${v.nViaggi||1}</div></div>
              <div><div style="font-size:8px;color:#9ca3af">€/km</div><div style="font-size:11px;font-weight:600">€${v.costoKm}</div></div>
              ${v.autostrada?`<div><div style="font-size:8px;color:#9ca3af">Autostrada</div><div style="font-size:11px;font-weight:600">€${v.autostrada}</div></div>`:""}
            </div>
            <div style="font-size:10px;color:#0369a1;background:#dbeafe;padding:4px 8px;border-radius:5px">${formulaTrasporto}</div>`
            :`<div style="font-size:10px;color:#d1d5db">Nessun trasporto</div>`}
          </div>

          <!-- Manodopera -->
          <div style="padding:12px 14px;background:#f0fdf4;">
            <div style="font-size:9px;color:#166534;font-weight:700;text-transform:uppercase;letter-spacing:.05em;margin-bottom:8px">👷 Manodopera</div>
            ${v.qtaOre?`
            <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px;margin-bottom:6px;">
              <div><div style="font-size:8px;color:#9ca3af">Quantità</div><div style="font-size:11px;font-weight:600">${v.qtaOre}</div></div>
              <div><div style="font-size:8px;color:#9ca3af">Costo unit.</div><div style="font-size:11px;font-weight:600">€${v.costoOra}</div></div>
              <div><div style="font-size:8px;color:#9ca3af">Totale</div><div style="font-size:13px;font-weight:800;color:#166534">€${fmt(c.totManodopera)}</div></div>
            </div>
            <div style="font-size:10px;color:#166534;background:#dcfce7;padding:4px 8px;border-radius:5px">${formulaMano}</div>`
            :`<div style="font-size:10px;color:#d1d5db">Nessuna manodopera</div>`}
          </div>
        </div>

        <!-- Riepilogo economico voce -->
        <div style="background:#fafafa;padding:10px 14px;">
          <div style="font-size:9px;color:#6b7280;font-weight:700;text-transform:uppercase;letter-spacing:.05em;margin-bottom:8px">📊 Riepilogo economico voce</div>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr 1fr 1fr;gap:8px;align-items:center;">
            <div style="background:#fff;border:1px solid #e5e7eb;border-radius:6px;padding:7px 10px;">
              <div style="font-size:8px;color:#9ca3af">Costo diretto</div>
              <div style="font-size:13px;font-weight:700;color:#374151">€${fmt(c.totDiretto)}</div>
            </div>
            <div style="background:#fff;border:1px solid #e5e7eb;border-radius:6px;padding:7px 10px;">
              <div style="font-size:8px;color:#9ca3af">Costi generali</div>
              <div style="font-size:13px;font-weight:700;color:#374151">${v.percCostiGenerali||0}%</div>
            </div>
            <div style="background:#fff;border:1px solid #e5e7eb;border-radius:6px;padding:7px 10px;">
              <div style="font-size:8px;color:#9ca3af">Importo C.G.</div>
              <div style="font-size:13px;font-weight:700;color:#374151">€${fmt(c.totCG)}</div>
            </div>
            <div style="background:#fef3c7;border:1px solid #fde68a;border-radius:6px;padding:7px 10px;">
              <div style="font-size:8px;color:#92400e">Markup (${v.percMarkup||0}%)</div>
              <div style="font-size:13px;font-weight:700;color:#d97706">+€${fmt(c.valoreOfferta-c.totCosti)}</div>
            </div>
            <div style="background:#d1fae5;border:1px solid #86efac;border-radius:6px;padding:7px 10px;">
              <div style="font-size:8px;color:#065f46;font-weight:700">Valore offerta</div>
              <div style="font-size:16px;font-weight:900;color:#059669">€${fmt(c.valoreOfferta)}</div>
            </div>
          </div>
        </div>

        ${v.note?`<div style="padding:7px 14px;background:#fef9f0;font-size:10px;color:#6b7280;border-top:1px solid #e5e7eb">📝 Note: ${v.note}</div>`:""}
      </div>`;
    }).join("");
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>RC ${rc?.numero||"BOZZA"} — ${form.oggetto}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;font-size:11px;color:#111;padding:12mm 16mm;}
      @media screen{body{max-width:1050px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4 landscape;}
      .hdr{border-bottom:3px solid #1e1b4b;padding-bottom:8px;margin-bottom:12px;display:flex;justify-content:space-between;align-items:flex-start;}
      .info{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:8px 12px;margin-bottom:12px;}
      .lbl{font-size:8px;color:#9ca3af;font-weight:700;text-transform:uppercase;display:block;margin-bottom:2px;}
      .val{font-size:11px;font-weight:600;}
      table{width:100%;border-collapse:collapse;}
      th{background:#1e1b4b;color:#fff;padding:7px 8px;font-size:9px;font-weight:700;text-align:left;}
      .footer{border-top:1px solid #e5e7eb;margin-top:12px;padding-top:6px;font-size:9px;color:#9ca3af;display:flex;justify-content:space-between;}
    </style></head><body>
    <div class="hdr">
      <div>${logoHtml}
        <div style="font-weight:700;margin-top:3px">${orgInfo?.nome||orgInfo?.name||""}</div>
        ${orgInfo?.indirizzo?`<div style="font-size:9px;color:#6b7280">${orgInfo.indirizzo}</div>`:""}
        ${orgInfo?.piva?`<div style="font-size:9px;color:#6b7280">P.IVA ${orgInfo.piva}</div>`:""}
      </div>
      <div style="text-align:right">
        <div style="font-size:14px;font-weight:800;color:#1e1b4b">RICHIESTA COMMERCIALE</div>
        <div style="font-size:16px;font-weight:900;color:#d97706;font-family:monospace">${rc?.numero||"BOZZA"}</div>
        <span style="padding:3px 10px;border-radius:20px;font-size:10px;font-weight:700;background:${STATI_RC[form.stato]?.bg};color:${STATI_RC[form.stato]?.col}">${STATI_RC[form.stato]?.label}</span>
      </div>
    </div>
    <div class="info" style="grid-template-columns:repeat(5,1fr);">
      <div><span class="lbl">Commessa</span><span class="val">${codiceCommessa?`[${codiceCommessa}] `:""}${projectName||"—"}</span></div>
      <div><span class="lbl">Cliente</span><span class="val">${clienteNome||"—"}</span></div>
      <div><span class="lbl">Sito</span><span class="val">${form.sito||"—"}</span></div>
      <div><span class="lbl">Data emissione</span><span class="val">${form.dataEmissione||"—"}</span></div>
      <div><span class="lbl">Assegnato a</span><span class="val">${form.assegnatarioNome||"—"}</span></div>
    </div>
    <div style="background:#fef3c7;border:1px solid #fde68a;border-radius:6px;padding:7px 12px;margin-bottom:10px;font-weight:600;color:#92400e">${form.oggetto}</div>
    ${vociHtml}
    <div style="background:#f0fdf4;border:2px solid #86efac;border-radius:8px;padding:10px 16px;display:flex;justify-content:space-between;align-items:center;margin-top:8px;">
      <div style="font-size:12px;font-weight:700;color:#374151">TOTALE COSTI: <span style="color:#374151">€${fmt(totCosti)}</span></div>
      <div style="font-size:16px;font-weight:900;color:#059669">TOTALE OFFERTA: €${fmt(totOff)}</div>
    </div>
    ${form.note?`<div style="margin-top:10px;padding:8px 12px;background:#fef3c7;border-radius:6px">${form.note}</div>`:""}
    <div class="footer">
      <span>Generato da Nexiora — ${dominioSito()}</span>
      <span>Compilato da: ${rc?.compilatoreNome||currentUser?.displayName||""}</span>
      <span>Stampato il ${new Date().toLocaleDateString("it-IT")}</span>
    </div>
    
    </body></html>`);
    win.document.close();
    setTimeout(()=>{
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
  };

  const {totCosti,totOff}=totali();
  const inp2={padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:7,fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none"};

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      {/* Header */}
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"12px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,flexWrap:"wrap",gap:8}}>
        <div>
          <div style={{fontSize:14,fontWeight:800,color:"#1e1b4b"}}>{rc?`Richiesta ${rc.numero}`:"Nuova richiesta commerciale"}</div>
          <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>
            {projectId
              ? <>{codiceCommessa&&`[${codiceCommessa}] `}{projectName}{clienteNome&&` · ${clienteNome}`}</>
              : <span style={{color:"#d97706",fontWeight:600}}>📋 Documento autonomo — non collegato a una commessa specifica</span>}
          </div>
        </div>
        <div style={{display:"flex",gap:8}}>
          {rc&&<button onClick={stampaPDF} style={{display:"flex",alignItems:"center",gap:5,padding:"7px 14px",border:"1px solid #2563eb",borderRadius:8,background:"#dbeafe",color:"#1d4ed8",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}><Printer size={13}/> PDF</button>}
          <button onClick={salva} disabled={saving} style={{padding:"7px 18px",border:"none",borderRadius:8,background:"#7c3aed",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            {saving?"Salvataggio...":"💾 Salva"}
          </button>
          <button onClick={onClose} style={{padding:"7px 10px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",cursor:"pointer"}}><X size={14}/></button>
        </div>
      </div>

      {/* sopralluoghi nati da questa richiesta (W416) */}
      {rc?.id && <div style={{padding:"0 20px",flexShrink:0}}><SopralluoghiDellaRC rc={{...rc, projectId: rc.projectId || projectId || "", projectName: rc.projectName || projectName || "", codiceCommessa: rc.codiceCommessa || codiceCommessa || ""}} orgId={orgId}/></div>}

      {!projectId && (
        <div style={{background:"#eff6ff",borderBottom:"1px solid #bfdbfe",padding:"8px 20px",flexShrink:0,fontSize:11.5,fontWeight:600,color:"#1e40af"}}>
          ℹ️ Questo modulo serve solo per potenziali clienti non ancora acquisiti. Se il cliente è già presente in Nexiora, crea la richiesta commerciale dall'interno della sua commessa.
        </div>
      )}

      {(orgInfo?.costiGeneraliDefault===undefined || orgInfo?.markupDefault===undefined) && (
        <div style={{background:"#fee2e2",borderBottom:"1px solid #fca5a5",padding:"7px 20px",flexShrink:0,fontSize:11,fontWeight:600,color:"#991b1b"}}>
          ⚠️ Questa organizzazione non ha ancora personalizzato i parametri commerciali in Impostazioni Aziendali — si stanno usando i valori di default (Margine/Utile 15%, Costi generali 10%). Verifica se vanno modificati per questa commessa.
        </div>
      )}

      {/* Testata */}
      <div style={{background:"#f9fafb",borderBottom:"1px solid #e5e7eb",padding:"12px 20px",display:"grid",gridTemplateColumns: projectId ? "2fr 1fr 1fr 1fr" : "1.8fr 1fr 1fr 1fr 1fr",gap:12,flexShrink:0}}>
        <div>
          <label style={{fontSize:10,color:"#9ca3af",fontWeight:600,display:"block",marginBottom:4}}>OGGETTO / DESCRIZIONE RICHIESTA *</label>
          <input style={{...inp2,width:"100%"}} value={form.oggetto} onChange={e=>upd("oggetto",e.target.value)} placeholder="Es. Fornitura in opera di pompetta e valvola di non ritorno"/>
        </div>
        <div>
          <label style={{fontSize:10,color:"#9ca3af",fontWeight:600,display:"block",marginBottom:4}}>SITO</label>
          <input style={{...inp2,width:"100%"}} value={form.sito||""} onChange={e=>upd("sito",e.target.value)}/>
        </div>
        {!projectId && (
          <div>
            <label style={{fontSize:10,color:"#9ca3af",fontWeight:600,display:"block",marginBottom:4}}>NOME POTENZIALE CLIENTE *</label>
            <input style={{...inp2,width:"100%"}} value={form.clienteNome||""} onChange={e=>upd("clienteNome",e.target.value)} placeholder="Nome dell'azienda/persona non ancora cliente"/>
          </div>
        )}
        <div>
          <label style={{fontSize:10,color:"#9ca3af",fontWeight:600,display:"block",marginBottom:4}}>DATA EMISSIONE</label>
          <input type="date" style={{...inp2,width:"100%"}} value={form.dataEmissione||""} onChange={e=>upd("dataEmissione",e.target.value)}/>
        </div>
        <div>
          <label style={{fontSize:10,color:"#d97706",fontWeight:700,display:"block",marginBottom:4}}>📤 ASSEGNA AL COMMERCIALE</label>
          <select style={{...inp2,width:"100%",borderColor:form.assegnatarioUid?"#d97706":"#e5e7eb"}} value={form.assegnatarioUid||""} onChange={e=>{
            const u=utenti.find(x=>x.uid===e.target.value);
            upd("assegnatarioUid",e.target.value);
            upd("assegnatarioNome",u?.displayName||"");
          }}>
            <option value="">— Nessuno —</option>
            {utenti.map(u=><option key={u.uid} value={u.uid}>{u.displayName}</option>)}
          </select>
        </div>

      </div>

      {/* Voci */}
      <div style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
        {voci.map((v,i)=>(
          <VoceCard key={i} voce={v} idx={i} nomeAzienda={orgInfo?.nome||orgInfo?.name||orgInfo?.name}
            onChange={nv=>setVoci(vs=>vs.map((x,j)=>j===i?nv:x))}
            onDelete={()=>setVoci(vs=>vs.filter((_,j)=>j!==i))}/>
        ))}
        <button onClick={addVoce} style={{display:"flex",alignItems:"center",gap:6,padding:"10px 16px",border:"2px dashed #d97706",borderRadius:10,background:"#fffbeb",color:"#d97706",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",width:"100%",justifyContent:"center"}}>
          <Plus size={14}/> Aggiungi voce di lavorazione
        </button>
      </div>

      {/* Footer totali */}
      <div style={{borderTop:"2px solid #e5e7eb",background:"#fff",padding:"12px 20px",display:"flex",alignItems:"center",justifyContent:"flex-end",gap:20,flexShrink:0}}>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:11,color:"#9ca3af",fontWeight:600,textTransform:"uppercase"}}>Totale costi</div>
          <div style={{fontSize:18,fontWeight:800,color:"#374151"}}>€{fmt(totCosti)}</div>
        </div>
        <div style={{width:1,height:40,background:"#e5e7eb"}}/>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:11,color:"#059669",fontWeight:700,textTransform:"uppercase"}}>Valore offerta totale</div>
          <div style={{fontSize:26,fontWeight:900,color:"#059669"}}>€{fmt(totOff)}</div>
        </div>
        <div style={{marginLeft:8}}>
          <label style={{fontSize:11,color:"#9ca3af",fontWeight:600,display:"block",marginBottom:4}}>Note generali</label>
          <input value={form.note||""} onChange={e=>upd("note",e.target.value)} placeholder="Note..."
            style={{padding:"6px 10px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12,fontFamily:"'Inter',sans-serif",width:240}}/>
        </div>
      </div>
    </div>
  );
}

export default function RichiestaCommercialeTab({ projectId, projectName, codiceCommessa, clienteNome, orgId }) {
  const { addToast } = useToast();
  const [richieste, setRichieste] = useState([]);
  const [orgInfo, setOrgInfo] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editRC, setEditRC] = useState(null);

  useEffect(()=>{
    if(!projectId||!orgId) return;
    return onSnapshot(
      query(collection(db,"richiesteCommerciali"),where("projectId","==",projectId),where("orgId","==",orgId)),
      snap=>setRichieste(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0)))
    );
  },[projectId,orgId]);

  useEffect(()=>{
    if(!orgId) return;
    getDoc(doc(db,"organizations",orgId)).then(snap=>{if(snap.exists())setOrgInfo(snap.data());});
  },[orgId]);

  const elimina=async id=>{
    if(!await confirm("Eliminare questa richiesta?",{type:"danger"})) return;
    await deleteDoc(doc(db,"richiesteCommerciali",id));
    addToast("Eliminata","info");
  };

  if(showForm||editRC){
    return <RCForm rc={editRC} projectId={projectId} projectName={projectName}
      codiceCommessa={codiceCommessa} clienteNome={clienteNome} orgId={orgId} orgInfo={orgInfo}
      onClose={()=>{setShowForm(false);setEditRC(null);}}/>;
  }

  return (
    <div style={{padding:20}}>
      <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:16,flexWrap:"wrap"}}>
        <h3 style={{fontSize:15,fontWeight:700,color:"#1e1b4b",margin:0}}>💼 Inoltro costi per offerta al cliente</h3>
        <button onClick={()=>setShowForm(true)} className="btn btn-primary"
          style={{display:"flex",alignItems:"center",gap:5,background:"#d97706",borderColor:"#d97706",padding:"5px 12px",fontSize:12}}>
          <Plus size={13}/> Nuova richiesta
        </button>
        <span style={{fontSize:11,color:"#9ca3af",marginLeft:"auto"}}>{richieste.length} richiesta/e</span>
      </div>

      {richieste.length===0?(
        <div style={{textAlign:"center",padding:60,color:"#9ca3af"}}>
          <div style={{fontSize:48,marginBottom:12,opacity:.15}}>💼</div>
          <div style={{fontSize:14,fontWeight:600}}>Nessuna richiesta commerciale</div>
          <div style={{fontSize:12,marginTop:4}}>Crea la prima richiesta per questa commessa</div>
        </div>
      ):(
        <div>
          {richieste.map((rc,i)=>{
            const stato=STATI_RC[rc.stato]||STATI_RC.bozza;
            return (
              <div key={rc.id} style={{border:"1px solid #e5e7eb",borderRadius:10,padding:"12px 16px",marginBottom:10,background:"#fff",display:"flex",alignItems:"center",gap:12,cursor:"pointer"}}
                onClick={()=>setEditRC(rc)}>
                <div style={{fontFamily:"monospace",fontWeight:800,color:"#d97706",fontSize:15,flexShrink:0}}>{rc.numero||"—"}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:700,color:"#111827"}}>{rc.oggetto}</div>
                  <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>{rc.dataEmissione} · {rc.compilatoreNome}{rc.assegnatarioNome && <span style={{marginLeft:8,color:"#d97706"}}>→ {rc.assegnatarioNome}</span>}</div>
                </div>
                <span style={{fontSize:11,padding:"3px 10px",borderRadius:10,background:stato.bg,color:stato.col,fontWeight:700,flexShrink:0}}>{stato.label}</span>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:11,color:"#9ca3af"}}>Valore offerta</div>
                  <div style={{fontSize:16,fontWeight:800,color:"#059669"}}>€{(rc.totaleOfferta||0).toLocaleString("it-IT",{minimumFractionDigits:2})}</div>
                </div>
                <button onClick={e=>{e.stopPropagation();elimina(rc.id);}} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5",padding:4}}>
                  <Trash2 size={14}/>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
